import { useCallback, useMemo, useState } from 'react';
import { CategoricalChartFunc } from 'recharts/types/chart/generateCategoricalChart';

import { BarPieceItem } from './bar-chart.types';
import {
  buildRenderTooltip,
  BuildSelectedRangeTooltipProps,
  BuildTooltipProps,
} from './build-render-tooltip';

export type BarChartControllerParams<T> = {
  items: BarPieceItem<T>[];
  buildTooltipProps: BuildTooltipProps<T>;
  buildSelectedRangeTooltipProps?: BuildSelectedRangeTooltipProps<T>;
};

export const useBarChartController = <T,>({
  items,
  buildTooltipProps,
  buildSelectedRangeTooltipProps,
}: BarChartControllerParams<T>) => {
  const [refAreaStartIndex, setRefAreaStartIndex] = useState<number | null>(null);
  const [refAreaEndIndex, setRefAreaEndIndex] = useState<number | null>(null);

  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);

  const onCellClick = useCallback(() => {
    setSelectedIndexes([]);
  }, []);

  const totalValue = useMemo(() => {
    return items.reduce((previousValue, currentValue) => previousValue + currentValue.value, 0);
  }, [items]);

  const handleOnMouseDown = useCallback<CategoricalChartFunc>((e) => {
    if (e.activeTooltipIndex === undefined) {
      return;
    }

    setRefAreaStartIndex(e.activeTooltipIndex);
    setRefAreaEndIndex(null);
  }, []);

  const handleOnMouseMove = useCallback<CategoricalChartFunc>(
    (e) => {
      if (refAreaStartIndex === null || e.activeTooltipIndex === undefined) {
        return;
      }

      setRefAreaEndIndex(e.activeTooltipIndex);
      setSelectedIndexes([]);
    },
    [refAreaStartIndex],
  );

  const handleOnMouseUp = useCallback<CategoricalChartFunc>(() => {
    if (refAreaStartIndex !== null && refAreaEndIndex !== null) {
      const minIndex = Math.min(refAreaStartIndex, refAreaEndIndex);
      const maxIndex = Math.max(refAreaStartIndex, refAreaEndIndex);

      const selectedRangeIndexes = items
        .slice(minIndex, maxIndex + 1)
        .map((item) => items.indexOf(item));

      if (selectedRangeIndexes.length > 1) {
        setSelectedIndexes(selectedRangeIndexes);
      }
    } else {
      onCellClick();
    }

    setRefAreaStartIndex(null);
    setRefAreaEndIndex(null);
  }, [items, onCellClick, refAreaEndIndex, refAreaStartIndex]);

  const renderTooltip = buildRenderTooltip(
    selectedIndexes,
    refAreaEndIndex,
    totalValue,
    buildTooltipProps,
    buildSelectedRangeTooltipProps,
    items,
  );

  return {
    handleOnMouseDown,
    handleOnMouseMove,
    handleOnMouseUp,
    selectedIndexes,
    refAreaEndIndex,
    refAreaStartIndex,
    renderTooltip,
    totalValue,
  };
};
